import React, { PureComponent } from 'react';
import { Query, graphql } from 'react-apollo';
import Fade from 'react-reveal';
import Head from 'next/head';
import styled from 'styled-components';
import { ParallaxProvider } from 'react-scroll-parallax';
import Watermark from '../../src/components/Parallax/Watermark';
import { withI18N } from '../../src/hoc';
import { Page, Main, Section } from '../../src/components/Layout';
import { Header } from '../../src/components/Header';
import { Container } from '../../src/components/Grid';
import Breadcrumb from '../../src/components/Breadcrumb';
import getOffices from '../../src/apollo/graphql/office/getOffices';
import { SmartMedia } from '../../src/components/Layout/Media';
import ContactForm from '../../src/components/ContactForm';
import sendMail from '../../src/apollo/graphql/contact/sendMail';
import setSubjectsContact from '../../src/utils/collection/setSubjectsContact';
import createContext from '../../src/utils/format/createContext';
import getMainSubjects from '../../src/apollo/graphql/contact/getMainSubjects';
import { Message } from '../../src/components/ContactForm/style';
import getPageByType from '../../src/apollo/graphql/page/getPageByType';

import { media } from '../../src/styles/media';
import Text from '../../src/components/Typography/Text';
import theme from '../../src/theme';

const FormContainer = styled.div`
  box-shadow: -1px -10px 22px 0 rgba(0, 0, 0, 0.08);
`;

const FormTitle = styled.h2`
  color: ${props => props.theme.color.black};
  font-family: ${props => props.theme.font.bold};
  font-size: 3.2rem;
  letter-spacing: 1.85px;
  margin-bottom: 2rem;
  text-transform: uppercase;
  text-align: center;
  padding-top: 5rem;

  ${media.tablet`
    font-size: 5.5rem;
    margin-bottom: 4rem;
    padding-top: 8rem;
  `};
`;

class Offices extends PureComponent {
  state = {
    displayMessage: true,
    messageType: null,
    message: null
  };

  handleSubmit = async ({ name, email, message, subjectId, captcha }) => {
    const { t, sendMail } = this.props;
    if (captcha) {
      const response = await sendMail({
        variables: {
          email,
          name,
          message,
          subject_id: subjectId
        }
      });

      const { data } = response;
      if (data.createRequest) {
        window.scroll(0, 0);
        this.setState({
          displayMessage: true,
          messageType: 'info',
          message: t('common:contact.alerts.confirmation')
        });
      } else {
        this.setState({
          displayMessage: true,
          messageType: 'error',
          message: t('common:contact.alerts.unknownError')
        });
      }
    } else {
      this.setState({
        displayMessage: true,
        messageType: 'error',
        message: t('common:contact.alerts.error')
      });
    }
  };

  handleEmptyFields = () => {
    const { t } = this.props;
    this.setState({
      displayMessage: true,
      messageType: 'error',
      message: t('common:contact.alerts.error')
    });
  };

  renderContactForm = () => {
    const { displayMessage, messageType, message } = this.state;
    const { t, i18n } = this.props;
    return (
      <Query query={getMainSubjects} context={createContext(i18n.language)}>
        {({ loading, error, data }) => {
          if (!loading && !error && data && data.getMainSubjects) {
            const subjects = data.getMainSubjects;
            const options = setSubjectsContact(subjects);
            const subjectsOffices = options.filter(
              option =>
                option.label.toLowerCase().includes('comercialización') ||
                option.label.toLowerCase().includes('commercialisation') ||
                option.label.toLowerCase().includes('marketing')
            );
            return (
              <Fade up>
                <FormContainer>
                  <Section>
                    <Container>
                      <Text
                        variant="heading2"
                        align="center"
                        as="h3"
                        marginBottom="50px"
                        paddingTop="50px"
                        color={theme.color.brand.primary}
                      >
                        {t('common:offices.contact')}
                      </Text>
                      {displayMessage && (
                        <Message type={messageType}>{message}</Message>
                      )}
                      <ContactForm
                        subjects={subjectsOffices}
                        onSubmit={this.handleSubmit}
                        onEmptyFields={this.handleEmptyFields}
                        info={false}
                      />
                    </Container>
                  </Section>
                </FormContainer>
              </Fade>
            );
          }

          return null;
        }}
      </Query>
    );
  };

  render() {
    const { t, i18n } = this.props;

    return (
      <Page>
        <Query
          query={getPageByType}
          variables={{ type: 'office' }}
          context={createContext(i18n.language)}
        >
          {({ loading, error, data }) => {
            if (!loading && !error && data && data.getPageByType) {
              const page = data.getPageByType;
              return (
                <Query
                  query={getOffices}
                  context={createContext(i18n.language)}
                >
                  {({ loading, error, data }) => {
                    if (!loading && !error && data && data.getOffices) {
                      const offices = data.getOffices;
                      return (
                        <div>
                          <Head>
                            <title key="title">Grand Hôtel-Dieu</title>
                            <link
                              rel="stylesheet"
                              href="/static/css/react-select-plus.css"
                            />
                          </Head>
                          <Header
                            title={page.detail.title}
                            image={page.img_header_desktop}
                            imageMobile={page.img_header_mobile}
                          />
                          <Main>
                            <Container>
                              <Breadcrumb
                                items={[
                                  {
                                    href: '/',
                                    label: t('common:nav.tabs.home')
                                  },
                                  { label: page.detail.title }
                                ]}
                              />
                            </Container>
                            {offices.map(
                              (office, index) =>
                                office.detail && (
                                  <SmartMedia
                                    key={office.id}
                                    title={office.detail.title}
                                    image={office.img_visual_desktop}
                                    logo={office.img_logo_desktop}
                                    logoMobile={office.img_logo_mobile}
                                    content={office.detail.content}
                                    link={office.detail.cta_url}
                                    linkLabel={office.detail.cta_label}
                                    socials={office.socialNetworks}
                                    reverse={index % 2 === 1}
                                  />
                                )
                            )}
                            {this.renderContactForm()}
                          </Main>
                        </div>
                      );
                    }
                    return null;
                  }}
                </Query>
              );
            }

            return null;
          }}
        </Query>
      </Page>
    );
  }
}

export default withI18N()(
  graphql(sendMail, {
    name: 'sendMail',
    options: ownProps => ({
      context: createContext(ownProps.i18n.language)
    })
  })(Offices)
);
